///
/// EarthBrowser
///
/// Created by Matt Giger on 9/9/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/use-toast"
import { findPaidSubscription } from "@/hooks/useEntitlements"
import { setProfileOpen, setSubscribeOpen } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { useGetUserSubscriptionQuery, useUploadAvatarMutation } from "@/redux/userApi"
import { datasetUrl } from "@/utils/datasetUrl"
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar"
import { format } from "date-fns"
import { BadgeCheckIcon, CameraIcon, Loader2, MailWarningIcon, SparklesIcon } from "lucide-react"
import { useState } from "react"

const cAvatarSize = 512

/**
 * Downscale and center-crop an image file to a square webp blob for avatar upload
 */
async function prepareAvatarImage(file: File): Promise<Blob> {
	const bitmap = await createImageBitmap(file)
	const side = Math.min(bitmap.width, bitmap.height)
	const size = Math.min(cAvatarSize, side)

	const canvas = document.createElement('canvas')
	canvas.width = size
	canvas.height = size
	const ctx = canvas.getContext('2d')
	if (!ctx) {
		throw new Error("Canvas not supported")
	}

	// Center-crop to a square, then scale down
	const sx = (bitmap.width - side) / 2
	const sy = (bitmap.height - side) / 2
	ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, size, size)
	bitmap.close()

	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) {
				resolve(blob)
			} else {
				reject(new Error("Failed to encode image"))
			}
		}, 'image/webp', 0.85)
	})
}

/**
 * Profile header with gradient-glow avatar (with working photo upload),
 * name, username, plan badge, email verification badge and member-since date.
 */
export function ProfileHeader() {
	const dispatch = useAppDispatch()
	const userProfile = useAppSelector((state) => state.user.userProfile)
	const { data: subscriptions } = useGetUserSubscriptionQuery()
	const [uploadAvatar, { isLoading: isUploading }] = useUploadAvatarMutation()
	const { toast } = useToast()
	const [imageHovered, setImageHovered] = useState(false)

	if (!userProfile) {
		return null
	}

	const fullName = `${userProfile.first_name} ${userProfile.last_name}`
	const initials = (userProfile.first_name?.[0] || "") + (userProfile.last_name?.[0] || "")
	const memberSince = userProfile.created_at ? format(new Date(userProfile.created_at), "MMMM yyyy") : undefined

	// Canonical paid rule (yearly / beta_yearly, active or trialing) so the
	// badge always agrees with the Phase 4 depth gates
	const paidSubscription = findPaidSubscription(subscriptions)

	async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0]
		e.target.value = ""
		if (!file) {
			return
		}
		try {
			const blob = await prepareAvatarImage(file)
			await uploadAvatar(blob).unwrap()
			toast({
				title: "Photo Updated",
				description: "Your profile photo has been changed",
			})
		} catch (error) {
			console.error("Avatar upload error:", error)
			toast({
				title: "Upload Failed",
				variant: "destructive",
				description: "Unable to update your profile photo. Please try again.",
			})
		}
	}

	return (
		<div className="flex flex-col items-center gap-2">
			{/* Avatar with gradient glow (design-system Avatar Pattern) */}
			<div className="relative group">
				<div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-500 rounded-full blur-md opacity-60 group-hover:opacity-90 transition-opacity duration-300" />
				<div
					className="relative"
					onMouseEnter={() => setImageHovered(true)}
					onMouseLeave={() => setImageHovered(false)}
				>
					<Avatar className="h-20 w-20 relative block">
						<AvatarImage
							className="h-20 w-20 rounded-full border-2 border-white/20 object-cover"
							src={datasetUrl(userProfile.image_url)}
							alt={fullName}
						/>
						<AvatarFallback className="h-20 w-20 rounded-full border-2 border-white/20 bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 flex items-center justify-center">
							<span className="text-2xl font-bold text-white">{initials}</span>
						</AvatarFallback>
					</Avatar>

					{/* Photo upload overlay */}
					<div className={`absolute inset-0 rounded-full bg-black/60 flex items-center justify-center transition-opacity duration-300 ${imageHovered || isUploading ? 'opacity-100' : 'opacity-0'}`}>
						{isUploading ? (
							<Loader2 className="h-6 w-6 animate-spin text-white" />
						) : (
							<Label htmlFor="avatar-upload" className="cursor-pointer">
								<div className="flex flex-col items-center gap-1 text-white">
									<CameraIcon className="h-5 w-5" />
									<span className="text-[10px] font-medium">Change Photo</span>
								</div>
							</Label>
						)}
						<Input
							id="avatar-upload"
							type="file"
							accept="image/*"
							className="hidden"
							disabled={isUploading}
							onChange={handleFileSelected}
						/>
					</div>
				</div>
			</div>

			{/* Name and username */}
			<div className="text-center space-y-0.5 min-w-0 max-w-full">
				<h1 className="text-lg font-bold text-foreground truncate">{fullName}</h1>
				<p className="text-xs text-muted-foreground font-medium truncate">@{userProfile.username}</p>
			</div>

			{/* Badges */}
			<div className="flex flex-wrap items-center justify-center gap-1.5">
				{subscriptions && (paidSubscription ? (
					<Badge className="border-transparent bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-500 hover:to-orange-500 gap-1">
						<SparklesIcon className="h-3 w-3" />
						{paidSubscription.name || "Pro"}
					</Badge>
				) : (
					<>
						<Badge variant="outline">Free</Badge>
						<Button
							size="sm"
							onClick={() => {
								dispatch(setProfileOpen(false))
								dispatch(setSubscribeOpen(true))
							}}
							className="h-6 rounded-full px-2.5 text-xs gap-1 shadow-md transition-all duration-300 hover:scale-105 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
						>
							<SparklesIcon className="h-3 w-3" />
							Upgrade
						</Button>
					</>
				))}
				{userProfile.email_verified ? (
					<Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15 gap-1" variant="outline">
						<BadgeCheckIcon className="h-3 w-3" />
						Verified
					</Badge>
				) : (
					<Badge className="border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-500/15 gap-1" variant="outline">
						<MailWarningIcon className="h-3 w-3" />
						Email Unverified
					</Badge>
				)}
			</div>
			{memberSince && (
				<p className="text-xs text-muted-foreground">Member since {memberSince}</p>
			)}
		</div>
	)
}
